import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

/* ===========================================================
   AUTH SHELL · minimalista futurista
   - Centrado, sin split-pane
   - Background: ambient orb único + grid muy sutil
   - Solo lo esencial: brand + form + footer micro-status
   =========================================================== */

const accent = '#00B4D8';
const accentSoft = 'rgba(0, 180, 216, 0.18)';
const success = '#06D6A0';
const mono = "'JetBrains Mono', 'Fira Code', 'Consolas', monospace";
const borderSoft = 'rgba(255, 255, 255, 0.06)';

/* ---------- animations ---------- */

const orbFloat = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0); }
  50%      { transform: translate3d(20px, -30px, 0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const dotPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(6, 214, 160, 0.55); }
  50%      { box-shadow: 0 0 0 5px rgba(6, 214, 160, 0); }
`;

const logoBreath = keyframes`
  0%, 100% { filter: drop-shadow(0 0 6px rgba(0, 180, 216, 0.35)); }
  50%      { filter: drop-shadow(0 0 12px rgba(0, 180, 216, 0.55)); }
`;

/* ---------- shell ---------- */

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #060B14;
  color: #E5F0FA;
  position: relative;
  overflow: hidden;
  padding: 1.5rem 1rem;
  font-family: inherit;
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;

  /* grid sutil con máscara radial para que sólo se vea cerca del centro */
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(ellipse 60% 50% at center, rgba(0, 0, 0, 0.85), transparent 80%);
`;

const Orb = styled.div`
  position: absolute;
  width: 480px;
  height: 480px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 180, 216, 0.22), transparent 70%);
  filter: blur(60px);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 0;
  animation: ${orbFloat} 16s ease-in-out infinite;
`;

/* ---------- top status (mini, una sola línea) ---------- */

const TopStatus = styled.div`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);

  &::after {
    content: '_';
    color: ${accent};
    margin-left: 2px;
    animation: ${blink} 1s step-end infinite;
  }
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${success};
  display: inline-block;
  animation: ${dotPulse} 2.4s ease-in-out infinite;
`;

/* ---------- card ---------- */

const Card = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.85rem;
  animation: ${fadeUp} 0.6s ease both;
`;

const Brand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.65rem;
`;

const LogoWrap = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    animation: ${logoBreath} 4s ease-in-out infinite;
  }
`;

const BrandName = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
`;

const FormBlock = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
`;

const Eyebrow = styled.div`
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: ${accent};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;

  &::before, &::after {
    content: '';
    width: 18px;
    height: 1px;
    background: ${accent};
    opacity: 0.5;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 500;
  text-align: center;
  letter-spacing: -0.005em;
  color: #ffffff;
`;

const Subtitle = styled.p`
  margin: -0.4rem 0 0;
  font-size: 0.85rem;
  color: rgba(229, 240, 250, 0.5);
  line-height: 1.5;
  text-align: center;
  max-width: 320px;
`;

const Footer = styled.div`
  font-family: ${mono};
  font-size: 0.55rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.28);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.25);
    display: inline-block;
  }
`;

/* ---------- helpers ---------- */

function pad(n) { return String(n).padStart(2, '0'); }
function formatUtc(d) {
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`;
}

function useLiveClock() {
  const [time, setTime] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return time;
}

export default function AuthLayout({ title, subtitle, eyebrow = 'SECURE_LOGIN', children }) {
  const time = useLiveClock();

  return (
    <Shell>
      <Background />
      <Orb />

      <TopStatus>
        <StatusDot />
        <span>{formatUtc(time)}</span>
      </TopStatus>

      <Card>
        <Brand>
          <LogoWrap>
            <img src="/logoarcana.png" alt="Arcana Vision" />
          </LogoWrap>
          <BrandName>Arcana Vision</BrandName>
        </Brand>

        <FormBlock>
          <Eyebrow>{eyebrow}</Eyebrow>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
          {children}
        </FormBlock>

        <Footer>
          <span>TLS 1.3</span>
          <i />
          <span>JWT</span>
          <i />
          <span>AES-256</span>
        </Footer>
      </Card>
    </Shell>
  );
}
